import { sortBy } from "lodash";
import { createSelector } from "reselect";

import {
  AttachmentSearchFields,
  AttachmentSearchGroupLabels,
} from "constants/attachmentSearch";
import {
  DocumentType,
  NVDDocumentTypeOptions,
  NVDRelatedDocumentType,
} from "constants/documentTypes";

import { filterBySaleType } from "lib/attachments";
import { isNVDRelatedDocumentType } from "lib/documentTypes";

import {
  getCurrentSale,
  getCurrentSpeciesId,
  selectCurrentDeploymentSales,
} from "./currentSale";
import {
  getBusinesses,
  getCandidateDeclarations,
  getConsignments,
  getEnvdDocuments,
  getFiles,
  getProperties,
} from "./root";
import {
  selectCandidateDeclarationIdByAttachmentIdLookup,
  selectCandidateDeclarationIdByEnvdIdLookup,
} from "./indexes";
import { selectIsCompleteByAttachmentIdLookup } from "./files";

import { getLivestockSaleId } from "lib/navigation";

interface File {
  type: DocumentType;
  consignment: string;
  envd?: { serialNumber?: string };
}

interface AdditionalProperty {
  PIC?: string;
}

const selectNvdRelatedFiles = createSelector([getFiles], files =>
  Object.values(files).filter(
    (file: File) => isNVDRelatedDocumentType(file.type) || !file.type,
  ),
);

const selectNvdRelatedConsignments = createSelector(
  [selectNvdRelatedFiles, getConsignments],
  (nvdRelatedFiles, consignments) =>
    nvdRelatedFiles
      .map((file: File) => consignments[file.consignment])
      .filter(Boolean),
);

const selectTypeOptions = createSelector([getCurrentSale], currentSale => {
  const saleType = currentSale?.sale_type;
  return NVDDocumentTypeOptions.filter(filterBySaleType(saleType)).map(
    option => ({
      label: option.label,
      value: {
        field: AttachmentSearchFields.Type,
        value: option.value,
      },
    }),
  );
});

const selectVendorPICOptions = createSelector(
  [selectNvdRelatedConsignments, getCandidateDeclarations, getProperties],
  (consignments, candidateDeclarations, properties) => {
    const vendorPICSet = new Set<string>();
    consignments.forEach((consignment: Consignment) => {
      if (consignment?.vendor_property_id) {
        const property = properties[consignment.vendor_property_id];
        if (property?.PIC) {
          vendorPICSet.add(property.PIC);
        }
      }
    });
    Object.values(candidateDeclarations)
      .map((cd: CandidateDeclaration) => cd.originPropertyId)
      .filter(Boolean)
      .forEach(propertyId => {
        const property = properties[propertyId];
        if (property?.PIC) {
          vendorPICSet.add(property.PIC);
        }
      });
    return sortBy(
      Array.from(vendorPICSet).map(pic => ({
        label: pic,
        value: {
          field: AttachmentSearchFields.VendorPIC,
          value: pic,
        },
      })),
      "label",
    );
  },
);

const selectAdditionalPICOptions = createSelector(
  [selectNvdRelatedFiles, getConsignments],
  (nvdRelatedFiles, consignments) => {
    const additionalPICSet = new Set<string>();
    nvdRelatedFiles.forEach((file: File) => {
      if (file.consignment) {
        const consignment = consignments[file.consignment];
        if (consignment?.additional_properties) {
          consignment.additional_properties.forEach(
            (prop: AdditionalProperty) => {
              if (prop?.PIC) {
                additionalPICSet.add(prop.PIC);
              }
            },
          );
        }
      }
    });
    return sortBy(
      Array.from(additionalPICSet).map(pic => ({
        label: pic,
        value: {
          field: AttachmentSearchFields.AdditionalPICs,
          value: pic,
        },
      })),
      "label",
    );
  },
);

const selectConsignmentOptions = createSelector(
  [selectNvdRelatedFiles, getConsignments, getBusinesses],
  (nvdRelatedFiles, consignments, businesses) => {
    const consignmentIdSet = new Set<string>();
    nvdRelatedFiles.forEach((file: File) => {
      if (file.consignment) {
        consignmentIdSet.add(file.consignment);
      }
    });
    return sortBy(
      Array.from(consignmentIdSet)
        .map(consignmentId => {
          const consignment = consignments[consignmentId];
          if (!consignment) {
            return null;
          }
          const vendor = businesses[consignment.vendor_id];
          const vendorName = vendor?.name || "Unknown";
          const shortCode = vendor?.shortCode ? ` ${vendor.shortCode}` : "";
          return {
            label: `${vendorName}${shortCode} (${consignment.quantity || 0} Hd)`,
            value: {
              field: AttachmentSearchFields.Consignment,
              value: consignmentId,
            },
          };
        })
        .filter(Boolean),
      "label",
    );
  },
);

const selectSerialNumberOptions = createSelector(
  [selectNvdRelatedConsignments, getCandidateDeclarations],
  (consignments, candidateDeclarations) => {
    const serialNumberSet = new Set<string>();
    Object.values(candidateDeclarations)
      .map((cd: CandidateDeclaration) => cd.serialNumber)
      .filter(Boolean)
      .forEach(serialNumber => serialNumberSet.add(serialNumber));
    Object.values(consignments)
      .map(c => c.NVD)
      .filter(Boolean)
      .forEach(serialNumber => {
        serialNumberSet.add(serialNumber);
      });
    return sortBy(
      Array.from(serialNumberSet).map(serialNumber => ({
        label: serialNumber,
        value: {
          field: AttachmentSearchFields.SerialNumber,
          value: serialNumber,
        },
      })),
      "label",
    );
  },
);

const selectNvdVersionOptions = createSelector(
  [selectNvdRelatedConsignments, getCandidateDeclarations],
  (consignments, candidateDecs) => {
    const nvdVersionSet = new Set<string>();
    consignments
      .map(c => c.declaration?.VERSION)
      .filter(Boolean)
      .forEach(nvdVersion => {
        nvdVersionSet.add(nvdVersion);
      });
    Object.values(candidateDecs)
      .map((cd: CandidateDeclaration) => cd.nvdVersion)
      .filter(Boolean)
      .forEach(nvdVersion => {
        nvdVersionSet.add(nvdVersion);
      });
    return sortBy(
      Array.from(nvdVersionSet).map(version => ({
        label: version,
        value: {
          field: AttachmentSearchFields.NVDVersion,
          value: version,
        },
      })),
      "label",
    );
  },
);

const isCompleteOptions = [
  {
    label: "Complete",
    value: {
      field: AttachmentSearchFields.IsComplete,
      value: true,
    },
  },
  {
    label: "Incomplete",
    value: {
      field: AttachmentSearchFields.IsComplete,
      value: false,
    },
  },
];

export const selectAttachmentSearchOptions = createSelector(
  [
    selectTypeOptions,
    selectVendorPICOptions,
    selectAdditionalPICOptions,
    selectConsignmentOptions,
    selectSerialNumberOptions,
    selectNvdVersionOptions,
  ],
  (
    typeOptions,
    vendorPICOptions,
    additionalPICOptions,
    consignmentOptions,
    serialNumberOptions,
    nvdVersionOptions,
  ) => [
    {
      label: AttachmentSearchGroupLabels[AttachmentSearchFields.Type],
      field: AttachmentSearchFields.Type,
      options: typeOptions,
    },
    {
      label: AttachmentSearchGroupLabels[AttachmentSearchFields.VendorPIC],
      field: AttachmentSearchFields.VendorPIC,
      options: vendorPICOptions,
    },
    {
      label: AttachmentSearchGroupLabels[AttachmentSearchFields.AdditionalPICs],
      field: AttachmentSearchFields.AdditionalPICs,
      options: additionalPICOptions,
    },
    {
      label: AttachmentSearchGroupLabels[AttachmentSearchFields.Consignment],
      field: AttachmentSearchFields.Consignment,
      options: consignmentOptions,
    },
    {
      label: AttachmentSearchGroupLabels[AttachmentSearchFields.SerialNumber],
      field: AttachmentSearchFields.SerialNumber,
      options: serialNumberOptions,
    },
    {
      label: AttachmentSearchGroupLabels[AttachmentSearchFields.NVDVersion],
      field: AttachmentSearchFields.NVDVersion,
      options: nvdVersionOptions,
    },
    {
      label: AttachmentSearchGroupLabels[AttachmentSearchFields.IsComplete],
      field: AttachmentSearchFields.IsComplete,
      options: isCompleteOptions,
    },
  ],
);

// Three types of files,
// a pure scanned image,
// an image which has been generated from accepting an eNVD into a sale,
// and an eNVD which has not been converted to an image
type ItemType = "Scanned Image" | "eNVD" | "Unused eNVD";

export interface AttachmentsViewItem {
  id: string /* For eNVD */ | number /* For Attachment */;
  type: ItemType;
  imageUrl: string | null;
  pdfUrl: string | null;
  thumbnailUrl?: string | null;
  consignment: Consignment | null;
  speciesId: number;
  candidateDeclarationId: string | null;
  documentType: NVDRelatedDocumentType | null;
  parseResult: ConsignmentAttachmentParseResult;
  deploymentId: number | null;
  deploymentSaleId: number | null;
  serialNumber: string | null;
  nvdVersion: string | null;
  isComplete: boolean | null;
  vendorPropertyId: string | null;
  progress?: number;
}

export const selectNvdAttachmentItems = createSelector(
  [
    getFiles,
    getEnvdDocuments,
    getConsignments,
    selectCandidateDeclarationIdByAttachmentIdLookup,
    selectCandidateDeclarationIdByEnvdIdLookup,
    getCandidateDeclarations,
    selectIsCompleteByAttachmentIdLookup,
    getCurrentSpeciesId,
    selectCurrentDeploymentSales,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Types for createSelector are...lacklustre. It hasn't been implemented in a way that handles the complexity of selectors and causes it raise a type error
  ] as any[],
  (
    attachmentByIdLookup: StateModelById<Attachment>,
    eNvdDocumentByIdLookup: StateModelById<EnvdDocument>,
    consignmentByIdLookup: StateModelById<Consignment>,
    candidateDeclarationIdByAttachmentIdLookup: Record<string, string>,
    candidateDeclarationIdByEnvdDocumentIdLookup: Record<string, string>,
    candidateDeclarationByIdLookup: StateModelById<CandidateDeclaration>,
    isCompleteByAttachmentIdLookup: Record<string, boolean>,
    speciesId: number,
    deploymentSaleByIdLookup: Record<string, DeploymentSale>,
  ): AttachmentsViewItem[] => {
    const attachmentViewItems: AttachmentsViewItem[] = [];

    const nvdRelatedAttachments = Object.values(attachmentByIdLookup).filter(
      (
        attachment: Attachment,
      ): attachment is Attachment & {
        type: NVDRelatedDocumentType | null;
      } => isNVDRelatedDocumentType(attachment.type) || !attachment.type,
    );
    const excludedEnvdDocumentIds = new Set<string>();

    nvdRelatedAttachments.forEach(attachment => {
      const consignment: Consignment =
        consignmentByIdLookup[attachment.consignment];
      const deploymentSale =
        deploymentSaleByIdLookup[attachment.deploymentSale];
      const deploymentId = deploymentSale ? deploymentSale.deployment_id : null;

      if (attachment.envdId) {
        excludedEnvdDocumentIds.add(attachment.envdId);

        // eNVD which was converted to an Attachment
        const candidateDeclarationId =
          candidateDeclarationIdByEnvdDocumentIdLookup[attachment.envdId];
        const candidateDeclaration: CandidateDeclaration | null =
          candidateDeclarationByIdLookup[candidateDeclarationId] || null;

        attachmentViewItems.push({
          id: attachment.id,
          type: "eNVD",
          candidateDeclarationId,
          consignment,
          speciesId,
          documentType: attachment.type,
          isComplete: isCompleteByAttachmentIdLookup[attachment.id],
          imageUrl: attachment.image_url,
          thumbnailUrl: attachment.thumbnail_url,
          pdfUrl: null,
          deploymentId,
          deploymentSaleId: attachment.deploymentSale,
          serialNumber: consignment
            ? consignment.NVD
            : candidateDeclaration?.serialNumber,
          nvdVersion: consignment
            ? consignment.declaration?.VERSION
            : candidateDeclaration?.nvdVersion,
          vendorPropertyId: consignment
            ? consignment.vendor_property_id
            : candidateDeclaration?.originPropertyId,
          parseResult: "success", // We can provide suggestions - it's based off an eNVD
          progress: null,
        });
      } else {
        // Pure scanned image, no associated eNVD document

        const candidateDeclarationId: string | null =
          candidateDeclarationIdByAttachmentIdLookup[attachment.id] || null;
        const candidateDeclaration: CandidateDeclaration | null =
          candidateDeclarationByIdLookup[candidateDeclarationId];

        attachmentViewItems.push({
          id: attachment.id,
          type: "Scanned Image",
          candidateDeclarationId,
          consignment,
          speciesId,
          documentType: attachment.type,
          isComplete: isCompleteByAttachmentIdLookup[attachment.id],
          imageUrl: attachment.image_url,
          thumbnailUrl: attachment.thumbnail_url,
          pdfUrl: null,
          deploymentId,
          deploymentSaleId: attachment.deploymentSale,
          serialNumber: consignment
            ? consignment.NVD
            : candidateDeclaration?.serialNumber,
          nvdVersion: consignment
            ? consignment.declaration?.VERSION
            : candidateDeclaration?.nvdVersion,
          vendorPropertyId: consignment
            ? consignment.vendor_property_id
            : candidateDeclaration?.originPropertyId,
          parseResult: attachment.latestParseResultStatus,
          progress: attachment.progress,
        });
      }
    });

    // Limit the eNVDs to the which haven't already been converted to attachments
    // in the sale. That would cause them to double up! This part is trying to
    // expose eNVDs which aren't yet in the sale, but probably should be, which
    // is why we include them in the first place.
    const applicableEnvdDocuments = Object.values(
      eNvdDocumentByIdLookup,
    ).filter(
      (eNvdDocument: EnvdDocument) =>
        !excludedEnvdDocumentIds.has(eNvdDocument.id) &&
        !eNvdDocument.excludedFromLivestockSaleIds.includes(
          getLivestockSaleId(),
        ),
    );

    const deploymentSaleIdByDeploymentId = Object.values(
      deploymentSaleByIdLookup,
    ).reduce<Record<number, number>>((acc, ds) => {
      acc[ds.deployment_id] = ds.deployment_sale_id;
      return acc;
    }, {});

    applicableEnvdDocuments.forEach((eNvdDocument: EnvdDocument) => {
      const deploymentSaleId =
        deploymentSaleIdByDeploymentId[eNvdDocument.deploymentId] || null;
      const candidateDeclarationId =
        candidateDeclarationIdByEnvdDocumentIdLookup[eNvdDocument.id] || null;
      const candidateDeclaration: CandidateDeclaration =
        candidateDeclarationByIdLookup[candidateDeclarationId];

      attachmentViewItems.push({
        id: eNvdDocument.id,
        type: "Unused eNVD",
        candidateDeclarationId,
        consignment: null,
        speciesId,
        documentType: DocumentType.NVD,
        isComplete: false,
        imageUrl: null,
        parseResult: null,
        pdfUrl: eNvdDocument.pdfUrl,
        thumbnailUrl: null,
        deploymentId: eNvdDocument.deploymentId,
        deploymentSaleId,
        serialNumber: candidateDeclaration?.serialNumber,
        nvdVersion: candidateDeclaration?.nvdVersion,
        vendorPropertyId: candidateDeclaration?.originPropertyId,
      });
    });

    return attachmentViewItems;
  },
);
